import { Trans } from '@lingui/react/macro';
import { cn } from '@utima/ui';
import { motion } from 'motion/react';
import pLimit from 'p-limit';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  useDropzone,
  type DropzoneOptions,
  type DropzoneProps,
  type DropzoneState,
} from 'react-dropzone';
import { v7 as uuidV7 } from 'uuid';

import { DropZoneContent } from './components/DropZoneContent';
import { FileItem } from './components/FileItem';
import type { FileUploadAdapter, UploadingFile } from './fileUploadTypes';

export interface FileUploadProps extends Omit<DropzoneProps, 'children'> {
  containerClassName?: string;
  dropZoneClassName?: string;
  children?: (dropzone: DropzoneState) => React.ReactNode;
  showFilesList?: boolean;
  showErrorMessage?: boolean;
  uploadAdapter: FileUploadAdapter;
  maxConcurrentUploads?: number;
  fullScreenDrop?: boolean;
  onFilesDropped?: () => void;
}

/** Imperative handle so a drop target outside this component's own drop
 * zone (e.g. the unified add-source modal treating its whole surface as a
 * drop target) can feed files through the same upload pipeline as a direct
 * drop on the visible zone. */
export interface FileUploadHandle {
  acceptFiles: (files: File[]) => void;
}

export const FileUpload = forwardRef<FileUploadHandle, FileUploadProps>(
  function FileUpload(
    {
      containerClassName,
      dropZoneClassName,
      children,
      showFilesList = true,
      showErrorMessage = true,
      uploadAdapter,
      maxConcurrentUploads = 5,
      fullScreenDrop = false,
      onFilesDropped,
      ...restProps
    },
    ref,
  ) {
    const [filesUploaded, setFilesUploaded] = useState<UploadingFile[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isWindowDragging, setIsWindowDragging] = useState(false);

    useEffect(() => {
      if (!fullScreenDrop) return;

      let dragDepth = 0;
      const isFileDrag = (event: DragEvent) =>
        Array.from(event.dataTransfer?.types ?? []).includes('Files');
      const enter = (event: DragEvent) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        dragDepth += 1;
        setIsWindowDragging(true);
      };
      const over = (event: DragEvent) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = restProps.disabled ? 'none' : 'copy';
        }
      };
      const leave = () => {
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) setIsWindowDragging(false);
      };
      const reset = () => {
        dragDepth = 0;
        setIsWindowDragging(false);
      };
      const drop = (event: DragEvent) => {
        if (!isFileDrag(event)) return;
        // Prevent navigation even when uploads are disabled.
        event.preventDefault();
        reset();
      };
      window.addEventListener('dragenter', enter);
      window.addEventListener('dragover', over);
      window.addEventListener('dragleave', leave);
      window.addEventListener('drop', drop);
      window.addEventListener('dragend', reset);
      window.addEventListener('blur', reset);

      return () => {
        window.removeEventListener('dragenter', enter);
        window.removeEventListener('dragover', over);
        window.removeEventListener('dragleave', leave);
        window.removeEventListener('drop', drop);
        window.removeEventListener('dragend', reset);
        window.removeEventListener('blur', reset);
      };
    }, [fullScreenDrop, restProps.disabled]);

    const uploadLimiter = useMemo(
      () => pLimit(maxConcurrentUploads),
      [maxConcurrentUploads],
    );

    const uploadFile = async (file: File, id: string) => {
      try {
        await uploadLimiter(() => uploadAdapter.upload(file));

        setFilesUploaded(prev =>
          prev.map(item =>
            item.id === id
              ? { ...item, status: 'completed' as const, progress: 100 }
              : item,
          ),
        );
      } catch (error) {
        console.error('Failed to upload file:', error);

        setFilesUploaded(prev =>
          prev.map(file =>
            file.id === id
              ? {
                  ...file,
                  status: 'error' as const,
                  errorMessage:
                    error instanceof Error ? error.message : 'Upload failed',
                }
              : file,
          ),
        );
      }
    };

    const acceptFiles = (files: File[]) => {
      const newFiles = files.map(file => ({
        id: uuidV7(),
        file,
        status: 'uploading' as const,
        progress: 0,
      }));

      setFilesUploaded(prev => [...newFiles, ...prev]);

      newFiles.forEach(item => {
        uploadFile(item.file, item.id);
      });
    };

    useImperativeHandle(ref, () => ({ acceptFiles }));

    const handleFileDrop: DropzoneOptions['onDrop'] = async (
      acceptedFiles,
      fileRejections,
      event,
    ) => {
      setIsWindowDragging(false);
      onFilesDropped?.();
      if (restProps.onDrop) {
        restProps.onDrop(acceptedFiles, fileRejections, event);
      } else {
        acceptFiles(acceptedFiles);

        if (fileRejections.length > 0) {
          let errorMsg = `Could not upload ${fileRejections[0].file.name}`;

          if (fileRejections.length > 1) {
            errorMsg =
              errorMsg + `, and ${fileRejections.length - 1} other files.`;
          }

          // Log errors
          for (const rejection of fileRejections) {
            console.error(rejection.errors);
          }

          setErrorMessage(errorMsg);
        } else {
          setErrorMessage('');
        }
      }
    };

    const handleCloseUploadedFile = (index: number) => {
      setFilesUploaded(prev => [
        ...prev.slice(0, index),
        ...prev.slice(index + 1),
      ]);
    };

    const deleteUploadedFile = async (index: number) => {
      const uploadedFile = filesUploaded[index];

      if (uploadAdapter.delete) {
        try {
          await uploadAdapter.delete(uploadedFile.file);
        } catch (error) {
          console.error('Failed to delete file:', error);

          return;
        }
      }

      handleCloseUploadedFile(index);
    };

    const dropzone = useDropzone({
      ...restProps,
      onDrop: handleFileDrop,
    });

    return (
      <div
        className={cn(
          'flex flex-col gap-2 h-full max-h-[300px] md:max-h-[500px]',
          containerClassName,
        )}
      >
        {fullScreenDrop &&
          isWindowDragging &&
          createPortal(
            <div
              {...dropzone.getRootProps({
                onClick: event => event.stopPropagation(),
              })}
              ref={undefined}
              className='pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center border-4 border-dashed border-primary bg-background/95 p-8 text-center text-xl font-medium'
              role='presentation'
            >
              {restProps.disabled ? (
                <Trans>File upload is currently unavailable.</Trans>
              ) : (
                <Trans>Drop files anywhere to upload</Trans>
              )}
            </div>,
            document.body,
          )}
        <div
          {...dropzone.getRootProps()}
          className={cn(
            'flex justify-center py-6 items-center w-full h-44 border-dashed border border-gray-200 rounded-lg hover:bg-accent hover:text-accent-foreground transition-all select-none cursor-pointer',
            dropZoneClassName,
          )}
        >
          <input {...dropzone.getInputProps()} />
          {children ? (
            children(dropzone)
          ) : (
            <DropZoneContent dropzone={dropzone} maxSize={restProps.maxSize} />
          )}
        </div>

        {showErrorMessage && errorMessage && (
          <span className='mt-3 text-xs text-red-600'>{errorMessage}</span>
        )}
        {showFilesList && (
          <motion.div
            className={cn('flex w-full flex-col gap-2 overflow-y-auto h-full')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {filesUploaded.map((file, index) => (
              <motion.div
                key={file.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <FileItem
                  file={file}
                  onDelete={
                    uploadAdapter.delete && (() => deleteUploadedFile(index))
                  }
                  onClose={() => handleCloseUploadedFile(index)}
                  autoCloseMs={2000}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    );
  },
);
